
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Card, CardContent, Grid, CircularProgress, useMediaQuery, useTheme, IconButton, Modal, Paper, Button, Divider } from '@mui/material';
import { styled } from '@mui/system';
import { GoogleMap, useJsApiLoader, Marker } from '@react-google-maps/api';
import Slider from 'react-slick';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import ShareIcon from '@mui/icons-material/Share';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import axios from 'axios';
import { useAuth } from './context/AuthContext';
import { useNavigate } from 'react-router-dom';

// Import slick-carousel styles
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;
const API_BASE_URL = 'https://api.athlete.academy.marsreel.com/api';

// --- STYLED COMPONENTS ---
const Root = styled(Box)({ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: '#1c1c1c', paddingTop: '64px' });
const ContentContainer = styled(Box)({ flex: 1, overflow: 'hidden', position: 'relative' });
const Footer = styled(Box)({ width: '100%', padding: '2rem 0', textAlign: 'center', fontSize: '0.9rem', color: '#b0b0b0', borderTop: '1px solid #303030', backgroundColor: '#141414', display: 'flex', justifyContent: 'center', alignItems: 'center' });
const OverlayCard = styled(Box)({ position: 'relative', height: '240px', width: '100%', borderRadius: '8px', overflow: 'hidden', backgroundColor: '#202020', color: '#fff', cursor: 'pointer', boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.5)', marginBottom: '1.5rem' });
const CardBackgroundImage = styled(Box)({ width: '100%', height: '100%', backgroundSize: 'cover', backgroundPosition: 'center', transition: 'transform 0.3s ease', '&:hover': { transform: 'scale(1.05)' } });
const CardTextOverlay = styled(Box)({ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '1rem 1.5rem', background: 'linear-gradient(to top, rgba(0,0,0,0.9) 0%, rgba(0,0,0,0) 100%)' });
const ModalPaper = styled(Paper)({ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '90%', maxWidth: '600px', backgroundColor: '#202020', color: '#fff', borderRadius: '12px', boxShadow: 24, outline: 'none', maxHeight: '90vh', display: 'flex', flexDirection: 'column' });
const ModalContent = styled(Box)({ padding: '1rem 2rem 2rem 2rem', overflowY: 'auto' });
const mapContainerStyle = { width: '100%', height: '100%' };
const darkMapStyle = [ { elementType: 'geometry', stylers: [{ color: '#212121' }] }, { elementType: 'labels.icon', stylers: [{ visibility: 'off' }] }, { elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] }, { elementType: 'labels.text.stroke', stylers: [{ color: '#212121' }] }, { featureType: 'administrative', elementType: 'geometry', stylers: [{ color: '#757575' }] }, { featureType: 'administrative.country', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] }, { featureType: 'administrative.land_parcel', stylers: [{ visibility: 'off' }] }, { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#bdbdbd' }] }, { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] }, { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#181818' }] }, { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] }, { featureType: 'poi.park', elementType: 'labels.text.stroke', stylers: [{ color: '#1b1b1b' }] }, { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#2c2c2c' }] }, { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#8a8a8a' }] }, { featureType: 'road.arterial', elementType: 'geometry', stylers: [{ color: '#373737' }] }, { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#3c3c3c' }] }, { featureType: 'road.highway.controlled_access', elementType: 'geometry', stylers: [{ color: '#4e4e4e' }] }, { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] }, { featureType: 'transit', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] }, { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#000000' }] }, { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#3d3d3d' }] } ];
const SlickArrow = ({ direction, onClick }) => ( <IconButton onClick={onClick} sx={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)', ...(direction === 'left' ? { left: 0 } : { right: 0 }), zIndex: 2, backgroundColor: 'rgba(30, 30, 30, 0.7)', color: 'white', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.8)' } }} > {direction === 'left' ? <ArrowBackIosNewIcon fontSize="small" /> : <ArrowForwardIosIcon fontSize="small" />} </IconButton> );

function formatEventDate(startStr, endStr) {
    if (!startStr || !endStr) return 'Date not available';
    const startDate = new Date(startStr);
    const endDate = new Date(endStr);
    const dateOptions = { weekday: 'short', month: 'short', day: 'numeric' };
    const timeOptions = { hour: 'numeric', minute: 'numeric', hour12: true };
    const datePart = startDate.toLocaleDateString(undefined, dateOptions);
    const startTimePart = startDate.toLocaleTimeString(undefined, timeOptions);
    const endTimePart = endDate.toLocaleTimeString(undefined, timeOptions);
    return `${datePart} • ${startTimePart} - ${endTimePart}`;
}

const DetailItem = ({ icon, label, value }) => (
    <Box sx={{ backgroundColor: '#2a2a2a', borderRadius: '10px', p: 2, display: 'flex', flexDirection: 'column', gap: 0.5, height: '100%' }}>
        {icon}
        <Typography variant="body2" sx={{ color: 'grey.500' }}>{label}</Typography>
        <Typography variant="body1" fontWeight="600" component="div">{value}</Typography>
    </Box>
);

const ClasspassMapPage = () => {
    const { isLoaded, loadError: mapLoadError } = useJsApiLoader({ id: 'google-map-script', googleMapsApiKey: GOOGLE_MAPS_API_KEY });
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    const [sessions, setSessions] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [map, setMap] = useState(null);
    const [selectedSessionId, setSelectedSessionId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [viewingSession, setViewingSession] = useState(null);
    const [sessionDetails, setSessionDetails] = useState(null);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);

    const fetchAllSessions = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`${API_BASE_URL}/sessions/upcoming`, { params: { lat: 34.0522, lon: -118.2437, maxDistance: 50 }, timeout: 8000 });
            if (Array.isArray(response.data)) {
                const sortedSessions = response.data.sort((a, b) => new Date(a.eventStartTime) - new Date(b.eventStartTime));
                setSessions(sortedSessions);
                if (sortedSessions.length > 0 && !selectedSessionId) {
                    setSelectedSessionId(sortedSessions[0]._id);
                }
            }
        } catch (err) {
            console.error("API Error:", err);
            setError("Failed to load sessions. Please check your connection and try again.");
        } finally {
            setIsLoading(false);
        }
        // ✅ THE ONLY CHANGE IS HERE: Removed selectedSessionId from the dependency array.
        // This stops the component from re-fetching data on every carousel swipe.
    }, []);

    useEffect(() => {
        fetchAllSessions();
    }, [fetchAllSessions]);

    useEffect(() => {
        if (viewingSession?._id && modalOpen) {
            const fetchDetails = async () => {
                setIsModalLoading(true);
                setModalError(null);
                try {
                    const response = await axios.get(`${API_BASE_URL}/sessions/${viewingSession._id}`);
                    setSessionDetails(response.data);
                } catch (err) {
                    setModalError("Could not load session details.");
                } finally {
                    setIsModalLoading(false);
                }
            };
            fetchDetails();
        }
    }, [viewingSession, modalOpen]);

    const handleCardClick = (session) => {
        setViewingSession(session);
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setViewingSession(null);
        setSessionDetails(null);
    };

    const handleReserve = async () => {
        if (!currentUser) { navigate('/login'); return; }
        setIsModalLoading(true);
        setModalError(null);
        try {
            await axios.post(`${API_BASE_URL}/sessions/reserve`, { firebaseUid: currentUser.uid, sessionId: viewingSession._id });
            alert('Reservation successful!');
            handleCloseModal();
            fetchAllSessions();
        } catch (err) {
            setModalError(err.response?.data?.error || "Failed to reserve session.");
        } finally {
            setIsModalLoading(false);
        }
    };

    const handleCancel = async () => {
        if (!currentUser) return;
        const isConfirmed = window.confirm("Are you sure you want to cancel your reservation?");
        if (isConfirmed) {
            setIsModalLoading(true);
            setModalError(null);
            try {
                await axios.post(`${API_BASE_URL}/sessions/cancel`, { firebaseUid: currentUser.uid, sessionId: viewingSession._id });
                alert('Cancellation successful!');
                handleCloseModal();
                fetchAllSessions();
            } catch (err) {
                setModalError(err.response?.data?.error || "Failed to cancel reservation.");
            } finally {
                setIsModalLoading(false);
            }
        }
    };

    const handleShare = () => {
        if (!sessionDetails) return;
        const url = `${window.location.origin}/session/${sessionDetails._id}`;
        navigator.clipboard.writeText(url).then(() => {
            alert('Session link copied to clipboard!');
        }).catch(err => {
            console.error('Failed to copy link:', err);
            alert('Failed to copy link.');
        });
    };

    const onLoad = useCallback(mapInstance => setMap(mapInstance), []);
    const onUnmount = useCallback(mapInstance => setMap(null), []);
    const handleCarouselChange = (index) => { const session = sessions[index]; setSelectedSessionId(session._id); if (map) map.panTo({ lat: session.latitude, lng: session.longitude }); };
    const center = { lat: sessions.length > 0 ? sessions[0].latitude : 34.0522, lng: sessions.length > 0 ? sessions[0].longitude : -118.2437 };
    const carouselSettings = { dots: true, infinite: sessions.length > 1, speed: 500, slidesToShow: 1, slidesToScroll: 1, centerMode: true, centerPadding: '20px', afterChange: handleCarouselChange, prevArrow: <SlickArrow direction="left" />, nextArrow: <SlickArrow direction="right" />, appendDots: dots => ( <Box sx={{ position: 'absolute', bottom: -30 }}> <ul style={{ margin: "0px", padding: "0px" }}> {dots} </ul> </Box> ), customPaging: i => ( <Box sx={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'grey.700' }} /> ) };

    const renderContent = () => {
        const CenteredMessage = ({ children }) => (<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}>{children}</Box>);
        if (mapLoadError) return <CenteredMessage><Typography color="error">Error loading maps.</Typography></CenteredMessage>;
        if (error) return <CenteredMessage><Typography color="error">{error}</Typography></CenteredMessage>;
        if (!isLoaded || isLoading) return <CenteredMessage><CircularProgress sx={{ color: '#12c099' }} /></CenteredMessage>;
        if (sessions.length === 0) return <CenteredMessage><Typography variant="h6">No upcoming sessions found.</Typography></CenteredMessage>;

        const SessionCardComponent = ({ session }) => {
            const formattedDate = formatEventDate(session.eventStartTime, session.eventEndTime);
            return (
                <OverlayCard onClick={() => handleCardClick(session)}>
                    <CardBackgroundImage sx={{ backgroundImage: `url(${session.venueImage1 || 'https://via.placeholder.com/400x240'})` }} />
                    <CardTextOverlay>
                        <Typography variant="h6" component="div" fontWeight="bold" noWrap>{session.name}</Typography>
                        <Typography variant="body2" sx={{ color: 'grey.300', mt: 0.5 }}>{formattedDate}</Typography>
                    </CardTextOverlay>
                </OverlayCard>
            );
        };
        const mapOptions = { styles: darkMapStyle, disableDefaultUI: true, zoomControl: true, zoomControlOptions: { position: window.google.maps.ControlPosition.TOP_RIGHT } };

        if (!isMobile) {
            return (
                <Grid container sx={{ height: '100%' }}>
                    <Grid item xs={12} md={7} sx={{ height: '100%', overflowY: 'auto', p: 3, backgroundColor: '#1c1c1c' }}>
                        <Typography variant="h4" fontWeight="bold" sx={{ mb: 2, color: '#fff' }}>Sessions Near You</Typography>
                        {sessions.map(session => <SessionCardComponent key={session._id} session={session} />)}
                    </Grid>
                    <Grid item xs={12} md={5} sx={{ height: '100%' }}>
                        <GoogleMap mapContainerStyle={mapContainerStyle} center={center} zoom={12} onLoad={onLoad} onUnmount={onUnmount} options={mapOptions}>
                            {sessions.map(session => <Marker key={session._id} position={{ lat: session.latitude, lng: session.longitude }} />)}
                        </GoogleMap>
                    </Grid>
                </Grid>
            );
        }

        return (
            <Box sx={{ height: '100%', width: '100%', position: 'relative' }}>
                <GoogleMap mapContainerStyle={mapContainerStyle} center={center} zoom={12} onLoad={onLoad} onUnmount={onUnmount} options={mapOptions}>
                    {sessions.map(session => ( <Marker key={session._id} position={{ lat: session.latitude, lng: session.longitude }} icon={{ path: window.google.maps.SymbolPath.CIRCLE, scale: selectedSessionId === session._id ? 10 : 6, fillColor: "#12c099", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 }} /> ))}
                </GoogleMap>
                <Box sx={{ position: 'absolute', bottom: 40, width: '100%', zIndex: 10 }}>
                    <style>{`.slick-dots li.slick-active div { background-color: #12c099 !important; }`}</style>
                    <Slider {...carouselSettings}>
                        {sessions.map((session) => ( <Box key={session._id} sx={{ px: 1.5 }}> <SessionCardComponent session={session} /> </Box> ))}
                    </Slider>
                </Box>
            </Box>
        );
    };

    const hasReserved = currentUser && sessionDetails?.reservedBy?.includes(currentUser.uid);
    const seatsRemaining = sessionDetails ? sessionDetails.capacity - sessionDetails.reservations : 0;
    const formattedModalDate = sessionDetails ? formatEventDate(sessionDetails.eventStartTime, sessionDetails.eventEndTime) : '';

    return (
        <Root>
            <ContentContainer>
                {renderContent()}
            </ContentContainer>

            <Modal open={modalOpen} onClose={handleCloseModal}>
                <ModalPaper>
                    {isModalLoading && !sessionDetails && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress sx={{ color: '#12c099' }} /></Box>}
                    {modalError && !sessionDetails && <Typography color="error" sx={{ p: 4, textAlign: 'center' }}>{modalError}</Typography>}
                    {sessionDetails && (
                        <>
                            <Box component="img" src={sessionDetails.venueImage1} sx={{ width: '100%', height: '200px', objectFit: 'cover', borderTopLeftRadius: '12px', borderTopRightRadius: '12px' }} />
                            <IconButton onClick={handleCloseModal} sx={{ position: 'absolute', top: 8, right: 8, color: 'white', backgroundColor: 'rgba(0,0,0,0.5)' }}><CloseIcon /></IconButton>

                            <ModalContent>
                                <Typography variant="h4" fontWeight="bold">{sessionDetails.name}</Typography>
                                <Typography variant="h6" sx={{ color: 'grey.400', mb: 2 }}>Hosted by {sessionDetails.hostName}</Typography>

                                <Grid container spacing={2} sx={{ my: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                        <DetailItem
                                            icon={<CalendarTodayIcon sx={{ color: 'grey.500' }} />}
                                            label="Date & Time"
                                            value={formattedModalDate}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DetailItem
                                            icon={<CreditCardIcon sx={{ color: 'grey.500' }} />}
                                            label="Cost"
                                            value={`${sessionDetails.creditsRequired} Credits`}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DetailItem
                                            icon={<LocationOnIcon sx={{ color: 'grey.500' }} />}
                                            label="Location"
                                            value={currentUser ? sessionDetails.address : "Log in to view"}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DetailItem
                                            icon={<EventSeatIcon sx={{ color: 'grey.500' }} />}
                                            label="Spots Left"
                                            value={seatsRemaining > 0 ? seatsRemaining : 'Fully Booked'}
                                        />
                                    </Grid>
                                </Grid>

                                <Typography fontWeight="bold" gutterBottom>About this clinic</Typography>
                                <Typography variant="body2" sx={{ color: 'grey.300' }}>{sessionDetails.description}</Typography>
                            </ModalContent>

                            <Box sx={{ p: 2, borderTop: '1px solid #303030', display: 'flex', gap: 1 }}>
                                <IconButton onClick={handleShare} sx={{ color: 'grey.400', border: '1px solid grey.700', borderRadius: '8px' }}>
                                    <ShareIcon />
                                </IconButton>
                                {hasReserved ? (
                                    <Button fullWidth variant="outlined" size="large" onClick={handleCancel} disabled={isModalLoading} sx={{ color: '#ff4d4d', borderColor: '#ff4d4d', '&:hover': { borderColor: '#ff1a1a' } }}>
                                        {isModalLoading ? <CircularProgress size={24} color="inherit"/> : 'Cancel Reservation'}
                                    </Button>
                                ) : (
                                    <Button fullWidth variant="contained" size="large" onClick={handleReserve} disabled={isModalLoading || seatsRemaining <= 0} sx={{ backgroundColor: '#12c099', color: '#fff', '&:hover': { backgroundColor: '#0f9f85' }, '&:disabled': { backgroundColor: 'grey.700' } }}>
                                        {isModalLoading ? <CircularProgress size={24} color="inherit"/> : (seatsRemaining <= 0) ? 'Fully Booked' : 'Reserve Session'}
                                    </Button>
                                )}
                            </Box>
                            {modalError && <Typography color="error" sx={{ textAlign: 'center', pb: 2 }}>{modalError}</Typography>}
                        </>
                    )}
                </ModalPaper>
            </Modal>

            {!isMobile && (
                <Footer>
                    <Typography variant="body2">© 2025 United Artificial Intelligence Company, Inc. All rights reserved.</Typography>
                </Footer>
            )}
        </Root>
    );
};

export default ClasspassMapPage;